import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService, ProfileService } from '@core/services';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { filter, tap } from 'rxjs';
import { LOG_IN_ROUTE } from 'src/app/shared/common/constants';
import { BISettingRoutes } from 'src/app/shared/common/route.conatant';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { environment } from 'src/environments/environment';
import { MenuTool } from '../../../interfaces/MenuTool.interface';
import { NavigationItem } from '../../../interfaces/navigationList.interface';
import {
  HomeButtonConfig,
  Profile,
} from '../../../interfaces/PageContainerConfiguration.interface';
import { PageContainerService } from '../../../services/page-container.service';

@Component({
  selector: 'z-mobile-main-sections-left-menu',
  standalone: true,
  imports: [CommonModule, MenuModule],
  templateUrl: './mobile-main-sections-left-menu.component.html',
  styleUrls: ['./mobile-main-sections-left-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileMainSectionsLeftMenuComponent {
  readonly pageContainerService = inject(PageContainerService);
  readonly router = inject(Router);
  private readonly _profileService = inject(ProfileService);
  private readonly navigationService = inject(NavigationService);
  private readonly _authService = inject(AuthService);

  navigationList = input.required<NavigationItem[]>();
  dashboardRoutes = input.required<NavigationItem[]>();
  settingsRoutes = input.required<NavigationItem>();
  homeButton = input.required<HomeButtonConfig>();
  profile = input.required<Profile>();
  settingURL = input<string>();
  tools = input.required<MenuTool[]>();
  hideTools = input<boolean>(false);

  onSelectSection = output<NavigationItem>();
  onToolsOpen = output<void>();

  isStaticButtonActive = signal<'home' | 'profile' | 'sitting' | ''>('');

  routerEvents$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    tap(event => {
      const url = event.urlAfterRedirects;
      this.checkStaticButtons(url);
      this.pageContainerService.expandMenuDrawer$.next(false);
    }),
  );

  activeSectionRoute$ = this.pageContainerService.activeSectionRoute;

  public profileMenuItems = signal<MenuItem[]>([]);
  public userName = computed<string>(() => {
    return this._authService.currentUser()?.fullName ?? 'Unknown User';
  });
  public userEmail = computed<string>(() => {
    return this._authService.currentUser()?.email ?? 'Unknown Email';
  });
  public userImage = computed<string>(
    () =>
      this._authService.currentUser()?.imageProfileDto?.imgDownloadUrl ??
      'assets/images/Avatars.png',
  );
  BISettingRoutes = signal<NavigationItem>(
    this.pageContainerService.filterNavigationList([BISettingRoutes])[0],
  );
  isStandAlonePortal = signal<boolean>(environment.isStandalonePortal);

  settingMenuItems = signal([
    {
      label: 'System Settings',
      disabled: false,
      command: () => {
        this.selectSection(this.settingsRoutes());
      },
    },
    {
      label: 'BI Portal Settings',
      disabled: false,
      command: () => {
        this.selectSection(this.BISettingRoutes());
      },
    },
  ]);

  ngAfterViewInit() {
    this.checkStaticButtons(this.router.url);
    this.setUserDetailsFromLocalStorage();
  }

  selectSection(item: NavigationItem) {
    if (item.children.length) {
      this.onSelectSection.emit(item);
    } else {
      if (item.isOutSideUrl) {
        // If the routeUrl is a full URL, open it directly
        if (
          item.routeUrl &&
          (item.routeUrl.startsWith('http://') ||
            item.routeUrl.startsWith('https://'))
        ) {
          window.open(item.routeUrl, '_self');
          return;
        }
        this.navigationService.getAndSetCurrentRoute();
        const service =
          item.outsideUrlTarget === 'core' ? '' : `/${item.outsideUrlTarget}/#`;
        const url = environment.localDomain + service + item.routeUrl;
        window.open(url, '_self');
        return;
      }
      this.pageContainerService.activeSectionRoute.next(item);
      this.router.navigate([item.routeUrl]);
    }
  }

  openTools() {
    this.onToolsOpen.emit();
  }

  goToHome(url: string) {
    this.pageContainerService.activeSectionRoute.next(null);
    this.router.navigate([url]);
  }

  navigateToProfile() {
    const url = this.profile()?.profileRoute;
    if (url?.length) this.router.navigate([url]);
  }

  navigateToSetting() {
    if (this.settingURL()?.length) this.router.navigate([this.settingURL()]);
  }

  checkStaticButtons(url: string) {
    if (url === this.homeButton().routeUrl) {
      this.isStaticButtonActive.set('home');
    } else if (url === this.profile().profileRoute) {
      this.isStaticButtonActive.set('profile');
    } else if (url === this.settingURL()) {
      this.isStaticButtonActive.set('sitting');
    } else {
      this.isStaticButtonActive.set('');
    }
  }

  private setUserDetailsFromLocalStorage(): void {
    const currentUser = this._authService.currentUser();
    if (currentUser) {
      this.profileMenuItems.set([
        {
          label: this.userName(),
          items: [
            {
              label: 'Profile Settings',
              icon: 'ph ph-user-circle',
              shortcut: '⌘+E',
              disabled: false,
              command: () => this.gotoProfile(),
            },
            {
              label: 'About',
              icon: 'ph ph-info',
              shortcut: '⌘+a',
              disabled: false,
              command: () => {
                this._profileService.showAboutDialog.set(true);
              },
            },
            {
              label: 'Log Out',
              icon: 'ph ph-sign-out',
              shortcut: '⌘+d',
              disabled: false,
              command: () => this.logOut(),
            },
          ],
        },
      ]);
    }
  }

  gotoProfile() {
    const url = environment.localDomain + this.profile().profileRoute;
    window.open(url, '_self');
  }

  logOut() {
    localStorage.clear();
    const url = environment.localDomain + LOG_IN_ROUTE;
    window.open(url, '_self');
  }
}
